import React, { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Handle, Position } from "@xyflow/react";
import type { Node as FlowNode, NodeProps } from "@xyflow/react";
import { Editor } from "primereact/editor";
import { Dialog } from "primereact/dialog";
import { shallow } from "zustand/shallow";
import { BsFillInfoCircleFill } from "react-icons/bs";
import { TbCircleDotFilled } from "react-icons/tb";
import { LuCheck } from "react-icons/lu";
import { CiSquareMinus, CiSquarePlus } from "react-icons/ci";
import { IoMdArrowDropdown, IoMdArrowDropup } from "react-icons/io";
import { MdOutlineAddTask, MdPerson, MdLocationOn, MdWarning } from "react-icons/md";
import { FaHandshake } from "react-icons/fa";
import { NodeData } from "../types/types";
import { useStore } from "../App/store";
import KanbanCard from "./KanbanCard";
import ResourceDialog from "./ResourceDialog";
import RiskDialog from "./RiskDialog";
import DecisionDialog from "./DecisionDialog";
import AddressCard from "./AddressCard";

const COLOR_PALETTE = [
  "#111827", // slate-900
  "#374151", // slate-700
  "#6B7280", // slate-500
  "#A1A1AA", // zinc-400
  "#EF4444", // red-500
  "#F97316", // orange-500
  "#F59E0B", // amber-500
  "#84CC16", // lime-500
  "#22C55E", // green-500
  "#06B6D4", // cyan-500
  "#3B82F6", // blue-500
  "#EC4899", // pink-500
  "#8B5CF6", // violet-500
];

let quillPaletteRegistered = false;

function ensureQuillColorPaletteRegistered(quill: any) {
  if (quillPaletteRegistered) return;

  const Quill = quill?.constructor;
  if (!Quill?.import || !Quill?.register) return;

  try {
    // PrimeReact/Quill default color format may use a whitelist that does not
    // include arbitrary hex colors. Register style attributors limited to our
    // palette so the toolbar selects actually apply formatting.
    const ColorStyle = Quill.import("attributors/style/color");
    const BackgroundStyle = Quill.import("attributors/style/background");

    if (ColorStyle) {
      ColorStyle.whitelist = COLOR_PALETTE;
      Quill.register(ColorStyle, true);
    }
    if (BackgroundStyle) {
      BackgroundStyle.whitelist = COLOR_PALETTE;
      Quill.register(BackgroundStyle, true);
    }

    quillPaletteRegistered = true;
  } catch {
    // Non-fatal: leave default behavior if Quill internals differ.
  }
}

function buildHeaderTemplate() {
  return (
    <div className="noteItem-toolbar">
      <span className="ql-formats">
        <button className="ql-bold" aria-label="Bold" />
        <button className="ql-italic" aria-label="Italic" />
        <button className="ql-underline" aria-label="Underline" />
      </span>

      <span className="ql-formats">
        <button className="ql-list" value="ordered" aria-label="Ordered list" />
        <button className="ql-list" value="bullet" aria-label="Bullet list" />
      </span>

      <span className="ql-formats">
        <select className="ql-color" aria-label="Text color">
          <option value="" />
          {COLOR_PALETTE.map((c) => (
            <option key={c} value={c} />
          ))}
        </select>
        <select className="ql-background" aria-label="Background color">
          <option value="" />
          {COLOR_PALETTE.map((c) => (
            <option key={c} value={c} />
          ))}
        </select>
      </span>

      <span className="ql-formats">
        <button className="ql-link" aria-label="Insert link" />
      </span>
    </div>
  );
}

function NoteItem({ id, data, selected }: NodeProps<FlowNode<NodeData>>) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<Editor | null>(null);
  const activeDrilldownTab = useStore((state) => state.activeDrilldownTab);
  const drilldownTabs = useStore((state) => state.drilldownTabs);
  const openDrilldownTab = useStore((state) => state.openDrilldownTab);
  const setActiveDrilldownTab = useStore((state) => state.setActiveDrilldownTab);
  const setPendingFocusNodeId = useStore((state) => state.setPendingFocusNodeId);
  const titleInputRef = useRef<HTMLTextAreaElement | null>(null);
  const notesIconClickTimeoutRef = useRef<number | null>(null);
  const markerTooltipTimeoutRef = useRef<number | null>(null);
  const [panelMode, setPanelMode] = useState<"hidden" | "readonly" | "editing">(
    "hidden",
  );
  const [isTitleEditing, setIsTitleEditing] = useState(false);
  const [showImageOverlay, setShowImageOverlay] = useState(false);
  const [showTaskDialog, setShowTaskDialog] = useState(false);
  const [showResourceDialog, setShowResourceDialog] = useState(false);
  const [showRiskDialog, setShowRiskDialog] = useState(false);
  const [showDecisionDialog, setShowDecisionDialog] = useState(false);
  const [showAddressDialog, setShowAddressDialog] = useState(false);
  const [titleDraft, setTitleDraft] = useState(data.label || "");
  const [showMarkerTooltip, setShowMarkerTooltip] = useState(false);
  const ignoreOutsideClickUntilRef = useRef<number>(0);

  const {
    nodes,
    setNodes,
    setIsDirty,
    updateNodeData,
    updateNodeLabel,
    hideChildNodes,
    showChildNodes,
  } = useStore(
    (state) => ({
      nodes: state.nodes,
      setNodes: state.setNodes,
      setIsDirty: state.setIsDirty,
      updateNodeData: state.updateNodeData,
      updateNodeLabel: state.updateNodeLabel,
      hideChildNodes: state.hideChildNodes,
      showChildNodes: state.showChildNodes,
    }),
    shallow,
  );

  const thisNode = useStore((state) => state.nodeMap.get(id));
  const isPresentationMode = useStore((state) => state.isPresentationMode);
  const presentWithShowHideButtons = useStore(
    (state) => state.presentWithShowHideButtons,
  );

  const { hasHiddenChildren, hasVisibleChildren } = useStore((state) => {
    const childIds = state.childrenMap.get(id) || [];
    let hidden = false;
    let visible = false;

    for (const childId of childIds) {
      const child = state.nodeMap.get(childId);
      if (child) {
        if (child.hidden) hidden = true;
        else visible = true;
        if (hidden && visible) break;
      }
    }

    return { hasHiddenChildren: hidden, hasVisibleChildren: visible };
  }, shallow);

  const notesHtml = data.notes || "";
  const normalizedNotesHtml = notesHtml
    .replace(/<p><br><\/p>/gi, "")
    .replace(/<p><\/p>/gi, "")
    .replace(/&nbsp;/gi, "")
    .trim();
  const hasNotes = normalizedNotesHtml.length > 0;

  // Detect Word document URLs
  const isWordDocumentUrl = (url: string): boolean => {
    if (!url) return false;
    const lower = url.toLowerCase();
    return (
      lower.endsWith(".docx") ||
      lower.endsWith(".doc") ||
      lower.includes(".docx?") ||
      lower.includes(".doc?") ||
      (lower.includes("sharepoint.com") && lower.includes(".docx")) ||
      lower.includes("office.live.com") ||
      lower.includes("1drv.ms")
    );
  };

  // Derive display type: explicit type, or infer "word" from first link
  const derivedType = useMemo(() => {
    if (data.type) return data.type as string;
    const firstUrl = data.links?.[0]?.url || "";
    if (isWordDocumentUrl(firstUrl)) return "word";
    return null;
  }, [data.type, data.links]);

  // Build satellite badge list — each item has content + optional double-click action
  const satelliteBadges: { key: string; content: ReactNode; action?: () => void }[] = useMemo(() => {
    const badges: { key: string; content: ReactNode; action?: () => void }[] = [];

    // Link badge — favicon fills the circle, or Word/generic link icon
    const linkUrl = data.links?.[0]?.url || "";
    if (linkUrl) {
      let linkContent: ReactNode;
      if (isWordDocumentUrl(linkUrl)) {
        linkContent = (
          <i className="pi pi-file-word" style={{ fontSize: 14, color: "#2b579a" }} />
        );
      } else {
        try {
          const domain = new URL(linkUrl).hostname;
          linkContent = (
            <img
              src={`https://www.google.com/s2/favicons?domain=${domain}&sz=32`}
              alt=""
              style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%" }}
            />
          );
        } catch {
          linkContent = <i className="pi pi-link" style={{ fontSize: 14 }} />;
        }
      }
      badges.push({
        key: "link",
        content: linkContent,
        action: () => window.open(linkUrl, "_blank", "noopener,noreferrer"),
      });
    }

    // Image badge — fills the full circle
    if (data.icon && data.icon.length > 0) {
      const isUrl =
        /^https?:\/\//.test(data.icon) ||
        data.icon.startsWith("data:image/") ||
        data.icon.endsWith(".png") ||
        data.icon.endsWith(".jpg") ||
        data.icon.endsWith(".jpeg");
      const imgContent = isUrl ? (
        <img
          src={data.icon}
          alt=""
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      ) : (
        <span style={{ fontSize: 15, lineHeight: 1 }}>{data.icon}</span>
      );
      badges.push({
        key: "image",
        content: imgContent,
        action: isUrl ? () => setShowImageOverlay(true) : undefined,
      });
    }

    // Type badge — semantic icon, opens the matching dialog
    if (derivedType) {
      const typeIconMap: Record<string, ReactNode> = {
        task: <MdOutlineAddTask size={16} />,
        resource: <MdPerson size={16} />,
        location: <MdLocationOn size={16} />,
        risk: <MdWarning size={16} />,
        decision: <FaHandshake size={15} />,
        word: <i className="pi pi-file-word" style={{ fontSize: 14, color: "#2b579a" }} />,
      };
      const typeActionMap: Record<string, () => void> = {
        task: () => setShowTaskDialog(true),
        resource: () => setShowResourceDialog(true),
        location: () => setShowAddressDialog(true),
        risk: () => setShowRiskDialog(true),
        decision: () => setShowDecisionDialog(true),
        word: () => {
          const url = data.links?.[0]?.url;
          if (url) window.open(url, "_blank", "noopener,noreferrer");
        },
      };
      badges.push({
        key: "type",
        content: typeIconMap[derivedType] ?? (
          <span style={{ fontSize: 9, fontWeight: 700 }}>{derivedType}</span>
        ),
        action: typeActionMap[derivedType],
      });
    }

    return badges;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.links, data.icon, derivedType]);

  // Place badges in an arc over the upper half of the dot
  const ARC_RADIUS = 24; // px from dot center
  const BADGE_SIZE = 20; // px diameter
  const SPREAD_DEG = 100; // total arc sweep (centred at top = -90°)

  const renderSatelliteBadges = () => {
    const total = satelliteBadges.length;
    if (total === 0) return null;

    return satelliteBadges.map(({ key, content, action }, i) => {
      // 1 badge → straight north; multiple → evenly spread over the arc
      let angleDeg: number;
      if (total === 1) {
        angleDeg = -90;
      } else {
        const startDeg = -90 - SPREAD_DEG / 2;
        const step = SPREAD_DEG / (total - 1);
        angleDeg = startDeg + i * step;
      }
      const angleRad = (angleDeg * Math.PI) / 180;
      const x = ARC_RADIUS * Math.cos(angleRad);
      const y = ARC_RADIUS * Math.sin(angleRad);

      return (
        <div
          key={key}
          className={`noteItem-satellite-badge${action ? " noteItem-satellite-badge--clickable" : ""}`}
          style={{
            left: `calc(50% + ${x}px - ${BADGE_SIZE / 2}px)`,
            top: `calc(50% + ${y}px - ${BADGE_SIZE / 2}px)`,
          }}
          onDoubleClick={undefined}
          onClick={
            action
              ? (e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  action();
                }
              : undefined
          }
        >
          {content}
        </div>
      );
    });
  };

  const typeLabel = derivedType ? derivedType : null;
  const hasImage = !!(data.icon && data.icon.length > 0);
  const hasStatus = !!(data.status && data.status.length > 0);

  const displayTitle = data.label?.trim() ? data.label : "Untitled";
  const markerType = data.marker || (data.complete ? "complete" : undefined);
  const markerLabel =
    markerType && markerType.length > 0
      ? markerType.charAt(0).toUpperCase() + markerType.slice(1)
      : "";

  const headerTemplate = useMemo(() => buildHeaderTemplate(), []);

  const focusQuill = (placeCaretAtEnd?: boolean) => {
    const quill = (editorRef.current as any)?.getQuill?.();
    if (quill?.focus) {
      quill.focus();
      if (placeCaretAtEnd && quill?.getLength && quill?.setSelection) {
        const length = quill.getLength();
        const pos = Math.max(0, (typeof length === "number" ? length : 0) - 1);
        quill.setSelection(pos, 0);
      }
      return;
    }
    const root = wrapperRef.current;
    const qlEditor = root?.querySelector(
      ".p-editor-container .ql-editor",
    ) as HTMLElement | null;
    qlEditor?.focus();
  };

  useEffect(() => {
    if (panelMode !== "editing") return;

    // Wait until PrimeReact Editor/Quill is mounted before focusing,
    // otherwise focus() can run too early and no caret appears.
    let raf1 = 0;
    let raf2 = 0;
    raf1 = window.requestAnimationFrame(() => {
      raf2 = window.requestAnimationFrame(() => {
        const quill = (editorRef.current as any)?.getQuill?.();
        ensureQuillColorPaletteRegistered(quill);
        focusQuill(true);
      });
    });

    return () => {
      if (raf1) window.cancelAnimationFrame(raf1);
      if (raf2) window.cancelAnimationFrame(raf2);
    };
  }, [panelMode]);

  const setNodeDraggable = (draggable: boolean) => {
    const current = useStore.getState().nodeMap.get(id);
    if (!current) return;
    if (current.draggable === draggable) return;

    setNodes(
      useStore
        .getState()
        .nodes.map((n) => (n.id === id ? { ...n, draggable } : n)),
    );
  };

  const focusTitleInput = () => {
    const input = titleInputRef.current;
    if (!input) return;
    input.style.height = "auto";
    input.style.height = `${input.scrollHeight}px`;
    input.focus();
    const len = input.value.length;
    input.setSelectionRange(len, len);
  };

  const resizeTitleInput = () => {
    const input = titleInputRef.current;
    if (!input) return;
    input.style.height = "auto";
    input.style.height = `${input.scrollHeight}px`;
  };

  useEffect(() => {
    if (data.label !== titleDraft && !isTitleEditing) {
      setTitleDraft(data.label || "");
    }
  }, [data.label, isTitleEditing, titleDraft]);

  useEffect(() => {
    if (data.autoTitleEdit) {
      setIsTitleEditing(true);
      setPanelMode("hidden");
      setNodeDraggable(false);

      ignoreOutsideClickUntilRef.current = Date.now() + 250;

      setTimeout(() => focusTitleInput(), 0);
      updateNodeData(id, { autoTitleEdit: false });
      return;
    }

    if (data.autoEdit) {
      setPanelMode("editing");
      setNodeDraggable(false);
      ignoreOutsideClickUntilRef.current = Date.now() + 250;
      updateNodeData(id, { autoEdit: false });
    }
  }, [data.autoEdit, data.autoTitleEdit, id, updateNodeData]);

  useEffect(() => {
    return () => {
      if (notesIconClickTimeoutRef.current != null) {
        window.clearTimeout(notesIconClickTimeoutRef.current);
        notesIconClickTimeoutRef.current = null;
      }
      if (markerTooltipTimeoutRef.current != null) {
        window.clearTimeout(markerTooltipTimeoutRef.current);
        markerTooltipTimeoutRef.current = null;
      }
    };
  }, []);

  const handleMarkerMouseEnter = () => {
    if (!markerType) return;
    if (markerTooltipTimeoutRef.current != null) {
      window.clearTimeout(markerTooltipTimeoutRef.current);
    }
    markerTooltipTimeoutRef.current = window.setTimeout(() => {
      setShowMarkerTooltip(true);
      markerTooltipTimeoutRef.current = null;
    }, 1000);
  };

  const handleMarkerMouseLeave = () => {
    if (markerTooltipTimeoutRef.current != null) {
      window.clearTimeout(markerTooltipTimeoutRef.current);
      markerTooltipTimeoutRef.current = null;
    }
    setShowMarkerTooltip(false);
  };

  useEffect(() => {
    // Intentionally do not auto-close on outside click.
    // Multiple note panels can remain open simultaneously; use the close button.
    return;
  }, [isTitleEditing, panelMode]);

  // Focus the Quill editor when opening in editing mode
  useEffect(() => {
    if (panelMode === "editing" && editorRef.current) {
      setTimeout(() => {
        const editor = editorRef.current;
        if (!editor) return;

        // Access the Quill instance
        const quill = (editor as any).getQuill?.();
        if (quill) {
          // Set focus and move cursor to end
          quill.focus();
          const length = quill.getLength();
          quill.setSelection(length, 0);
        }
      }, 100);
    }
  }, [panelMode]);

  const openPanelReadonly = () => {
    setPanelMode((prev) => (prev === "hidden" ? "readonly" : prev));
  };

  const openPanelEditing = () => {
    setPanelMode("editing");
    setNodeDraggable(false);
    ignoreOutsideClickUntilRef.current = Date.now() + 250;
  };

  const closePanel = () => {
    setPanelMode("hidden");
    setNodeDraggable(true);
  };

  const toggleNotesPreview = () => {
    if (!hasNotes) return;

    if (panelMode === "readonly") {
      closePanel();
      return;
    }

    setPanelMode("readonly");
    setNodeDraggable(true);
  };

  const commitTitle = (nextTitle: string) => {
    const trimmed = (nextTitle || "").trim();
    updateNodeLabel(id, trimmed);
    setIsDirty(true);
  };

  const beginTitleEdit = () => {
    if (data.userRole === "viewer") return;
    setIsTitleEditing(true);
    setNodeDraggable(false);
    ignoreOutsideClickUntilRef.current = Date.now() + 250;
    setTimeout(() => {
      resizeTitleInput();
      focusTitleInput();
    }, 0);
  };

  const finishTitleEdit = () => {
    setIsTitleEditing(false);
    setNodeDraggable(true);
    commitTitle(titleDraft);
  };

  const handleTextChange = (htmlValue: string) => {
    updateNodeData(id, { notes: htmlValue });
    setIsDirty(true);
  };

  const handleWheelOverEditor = (e: React.WheelEvent) => {
    // Ensure wheel scrolls the note content (not the canvas zoom).
    e.stopPropagation();
    e.preventDefault();

    const root = wrapperRef.current;
    const qlEditor = root?.querySelector(
      ".p-editor-container .ql-editor",
    ) as HTMLElement | null;
    if (!qlEditor) return;

    qlEditor.scrollTop += e.deltaY;
  };

  const handleWheelOverPreview = (e: React.WheelEvent<HTMLDivElement>) => {
    // Ensure wheel scrolls preview content (not the canvas zoom).
    e.stopPropagation();
    e.preventDefault();
    e.currentTarget.scrollTop += e.deltaY;
  };

  const handleHideShowClick = (event: React.MouseEvent) => {
    const showAllLevels = event.shiftKey;
    if (hasHiddenChildren) {
      showChildNodes(id, showAllLevels);
    } else {
      hideChildNodes(id);
      setTimeout(() => {
        document.getSelection()?.removeAllRanges();
        titleInputRef.current?.blur();
      }, 0);
    }
  };

  return (
    <div
      ref={wrapperRef}
      className={`noteItemGraph-root ${panelMode !== "hidden" ? "noteItemGraph-open" : ""} ${selected ? "noteItemGraph-selected" : ""}`}
      onDoubleClick={(e) => {
        // Avoid graph-level double-click behaviors.
        e.stopPropagation();
      }}
    >
      {/* Drill-down indicator - shows arrow based on context */}
      {(() => {
        const isDrilldownFocus =
          activeDrilldownTab &&
          drilldownTabs.some(
            (tab) => tab.id === activeDrilldownTab && tab.nodeId === id,
          );

        const handleDrilldownIconClick = (e: React.MouseEvent) => {
          e.preventDefault();
          e.stopPropagation();
          openDrilldownTab(id);
        };

        const handleDrillupIconClick = (e: React.MouseEvent) => {
          e.preventDefault();
          e.stopPropagation();
          setPendingFocusNodeId(id);
          const currentTab = drilldownTabs.find(
            (t) => t.id === activeDrilldownTab,
          );
          const parentTabId = (currentTab as any)?.parentTabId ?? null;
          const parentExists = parentTabId
            ? drilldownTabs.some((t) => t.id === parentTabId)
            : false;
          setActiveDrilldownTab(parentExists ? parentTabId : null);
        };

        if (isDrilldownFocus) {
          return (
            <div
              className="drilldown-indicator note-drilldown"
              onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
              onClick={handleDrillupIconClick}
            >
              <i
                className="pi pi-arrow-up drilldown-icon drilldown-up"
                title="Go to parent layer"
              />
            </div>
          );
        } else if (data.isDrilldownTab) {
          return (
            <div
              className="drilldown-indicator note-drilldown"
              onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
              onClick={handleDrilldownIconClick}
            >
              <i
                className="pi pi-arrow-down drilldown-icon"
                title="Open drill-down tab"
              />
            </div>
          );
        }
        return null;
      })()}
      <div
        className="noteItem-dot"
        style={
          data.backgroundColor &&
          data.backgroundColor !== "#ffffff" &&
          data.backgroundColor !== "#fff"
            ? {
                background: data.backgroundColor,
                borderColor: data.backgroundColor,
                color: data.color || "#ffffff",
              }
            : undefined
        }
        onMouseEnter={handleMarkerMouseEnter}
        onMouseLeave={handleMarkerMouseLeave}
        onPointerDown={handleMarkerMouseLeave}
      >
        {/* Connection handles are nested inside the dot so edges always
            attach to its actual visual center, instead of relying on a
            fixed pixel offset from the node root. */}
        <Handle type="target" position={Position.Top} className="target" />
        <Handle type="source" position={Position.Top} className="source" />
        <TbCircleDotFilled className="noteItem-dotIcon" aria-hidden="true" />
        {/* Arc satellite badges */}
        {renderSatelliteBadges()}
        {hasNotes && (
          <div className="note-notes-indicator-left">
            <button
              type="button"
              className="note-notes-icon"
              aria-label="Show notes"
              aria-expanded={panelMode !== "hidden"}
              onClick={(e) => {
                e.stopPropagation();
                toggleNotesPreview();
              }}
              onPointerDown={(e) => {
                e.stopPropagation();
              }}
            >
              <BsFillInfoCircleFill aria-hidden="true" />
            </button>
          </div>
        )}
        {/* Show/hide children — anchored to right edge of the dot */}
        {(!isPresentationMode || presentWithShowHideButtons) &&
          (hasVisibleChildren || hasHiddenChildren) && (
            <div
              className="note-show-hide-area"
              onPointerDown={(e) => e.stopPropagation()}
            >
              {hasVisibleChildren && !hasHiddenChildren ? (
                <CiSquareMinus
                  className="show-hide-icon show-hide-icon-minus"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleHideShowClick(e);
                  }}
                />
              ) : (
                <CiSquarePlus
                  className="show-hide-icon show-hide-icon-plus"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleHideShowClick(e);
                  }}
                />
              )}
            </div>
          )}
      </div>
      {markerType && (
        <div className="noteItem-markerAnchor">
          <div
            className={`noteItem-marker-badge marker-${markerType}`}
            aria-label={markerLabel}
          >
            {markerType === "question" ? (
              "?"
            ) : markerType === "alert" ? (
              "!"
            ) : markerType === "strength" ? (
              <i className="pi pi-thumbs-up" aria-hidden="true" />
            ) : markerType === "weakness" ? (
              <i className="pi pi-thumbs-down" aria-hidden="true" />
            ) : markerType === "opportunity" ? (
              <i className="pi pi-bolt" aria-hidden="true" />
            ) : markerType === "threat" ? (
              <i className="pi pi-exclamation-triangle" aria-hidden="true" />
            ) : (
              <LuCheck size={14} aria-hidden="true" />
            )}
          </div>
          {showMarkerTooltip && (
            <div className="noteItem-marker-tooltip">{markerLabel}</div>
          )}
        </div>
      )}

      <div className="noteItem-titleArea nodrag nopan nowheel">
        {isTitleEditing ? (
          <textarea
            ref={titleInputRef}
            className="noteItem-titleInput"
            value={titleDraft}
            placeholder="Untitled"
            title={titleDraft?.trim() ? titleDraft : displayTitle}
            onChange={(e) => {
              setTitleDraft(e.target.value);
              resizeTitleInput();
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.stopPropagation();
                return;
              }
              if (e.key === "Escape") {
                e.preventDefault();
                e.stopPropagation();
                setIsTitleEditing(false);
                setTitleDraft(data.label || "");
                setNodeDraggable(true);
              }
            }}
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
            onInput={() => resizeTitleInput()}
            onBlur={() => {
              finishTitleEdit();
            }}
            rows={1}
          />
        ) : (
          <div
            className="noteItem-titleText"
            title={displayTitle}
            onClick={(e) => {
              e.stopPropagation();
            }}
            onDoubleClick={(e) => {
              e.stopPropagation();
              beginTitleEdit();
            }}
          >
            {displayTitle}
          </div>
        )}
      </div>

      {panelMode !== "hidden" && (
        <div
          className={`noteItem-panel ${panelMode === "editing" ? "noteItem-panelEditing" : "noteItem-panelReadonly"} nodrag nopan nowheel`}
          onWheelCapture={
            panelMode === "editing"
              ? handleWheelOverEditor
              : handleWheelOverPreview
          }
          onPointerDown={(e) => {
            // Prevent ReactFlow node/pane handlers.
            e.stopPropagation();
          }}
          onPointerDownCapture={(e) => {
            if (panelMode !== "editing") return;

            // ReactFlow may listen in capture-phase; block it for toolbar interactions
            // so format buttons toggle on single click.
            const target = e.target as HTMLElement | null;
            const inToolbar =
              !!target?.closest(".p-editor-toolbar") ||
              !!target?.closest(".ql-toolbar");
            if (!inToolbar) return;

            const isNativeSelect =
              target?.tagName === "SELECT" || !!target?.closest("select");
            const isButton =
              target?.tagName === "BUTTON" || !!target?.closest("button");

            // Only preventDefault for toolbar buttons.
            // Quill's picker label/options rely on default pointer behavior to open.
            if (isButton && !isNativeSelect) {
              e.preventDefault();
            }

            e.stopPropagation();
          }}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            className="noteItem-panelClose"
            aria-label="Close"
            onClick={(e) => {
              e.stopPropagation();
              closePanel();
            }}
            onPointerDown={(e) => {
              // Prevent ReactFlow selection/drag on close click.
              e.stopPropagation();
            }}
          >
            {"\u00D7"}
          </button>
          {panelMode === "editing" ? (
            <Editor
              ref={editorRef}
              value={notesHtml}
              onTextChange={(e) => handleTextChange(e.htmlValue || "")}
              headerTemplate={headerTemplate}
              className="noteItem-editorControl"
            />
          ) : (
            <div
              className="noteItem-panelPreview"
              onWheelCapture={handleWheelOverPreview}
              dangerouslySetInnerHTML={{ __html: notesHtml || "<p></p>" }}
              onDoubleClick={(e) => {
                // Double-click in panel enters edit mode.
                e.stopPropagation();
                openPanelEditing();
              }}
            />
          )}
        </div>
      )}

      {/* ── Badge-triggered dialogs ──────────────────────────────────── */}

      {/* Image lightbox */}
      {showImageOverlay && data.icon && (
        <Dialog
          visible={showImageOverlay}
          onHide={() => setShowImageOverlay(false)}
          header="Image"
          style={{ width: "auto", maxWidth: "90vw" }}
          modal
        >
          <img
            src={data.icon}
            alt={data.label}
            style={{ maxWidth: "80vw", maxHeight: "80vh", display: "block" }}
          />
        </Dialog>
      )}

      {/* Task dialog */}
      {showTaskDialog && (
        <KanbanCard
          visible={showTaskDialog}
          nodeTask={
            data.task || {
              id: `task-${id}`,
              title: data.label,
              nodeTitle: data.label,
              status: "todo",
            }
          }
          setShowKanbanCard={setShowTaskDialog}
          handleAddUpdateTask={(taskId, title, nodeTitle, status, summary, priority, tags, assignee, estimate, rankId, color, className, startDate, endDate) => {
            updateNodeData(id, {
              task: { id: taskId, title, nodeTitle, status, summary, priority, tags, assignee, estimate, rankId, color, className, startDate, endDate },
            });
            setIsDirty(true);
          }}
          handleDeleteTask={() => {
            updateNodeData(id, { task: undefined });
            setIsDirty(true);
          }}
        />
      )}

      {/* Resource dialog */}
      {showResourceDialog && (
        <ResourceDialog
          visible={showResourceDialog}
          resourceData={
            data.resource || {
              name: data.label, role: "", department: "",
              email: "", phone: "", availability: 100, skills: "", notes: "",
            }
          }
          setShowResourceDialog={setShowResourceDialog}
          handleSaveResource={(resourceData) => {
            updateNodeData(id, { resource: resourceData });
            setIsDirty(true);
          }}
          handleDeleteResource={
            data.resource
              ? () => { updateNodeData(id, { resource: undefined }); setIsDirty(true); setShowResourceDialog(false); }
              : undefined
          }
        />
      )}

      {/* Risk dialog */}
      {showRiskDialog && (
        <RiskDialog
          visible={showRiskDialog}
          riskData={
            data.risk || {
              name: data.label, description: "", category: "",
              impact: "Medium", likelihood: "Medium", severity: "Medium",
              mitigation: "", owner: "", status: "Open",
            }
          }
          setShowRiskDialog={setShowRiskDialog}
          handleSaveRisk={(riskData) => {
            updateNodeData(id, { risk: riskData });
            setIsDirty(true);
          }}
          handleDeleteRisk={
            data.risk
              ? () => { updateNodeData(id, { risk: undefined }); setIsDirty(true); setShowRiskDialog(false); }
              : undefined
          }
        />
      )}

      {/* Decision dialog */}
      {showDecisionDialog && (
        <DecisionDialog
          visible={showDecisionDialog}
          decisionData={
            data.decision || {
              name: data.label, description: "", category: "Strategic",
              status: "Pending", decisionMaker: "", stakeholders: "",
              criteria: "", alternatives: "", selectedOption: "",
              rationale: "", implications: "",
            }
          }
          setShowDecisionDialog={setShowDecisionDialog}
          handleSaveDecision={(decisionData) => {
            updateNodeData(id, { decision: decisionData });
            setIsDirty(true);
          }}
          handleDeleteDecision={
            data.decision
              ? () => { updateNodeData(id, { decision: undefined }); setIsDirty(true); setShowDecisionDialog(false); }
              : undefined
          }
        />
      )}

      {/* Address/location dialog */}
      {showAddressDialog && (
        <AddressCard
          visible={showAddressDialog}
          nodeAddress={
            data.address || {
              id, nodeTitle: data.label, name: data.label,
              street: "", zip_code: "", city: "",
              Phone: "", email: "", vat: "", employees: "", notes: "",
            }
          }
          setShowAddressCard={setShowAddressDialog}
          handleAddUpdateAddress={(addrId, nodeTitle, name, street, zip_code, city, Phone, email, vat, employees, notes, latitude, longitude) => {
            updateNodeData(id, {
              address: { id: addrId || id, nodeTitle, name, street, zip_code, city, Phone, email, vat, employees, notes, latitude, longitude },
            });
            setIsDirty(true);
          }}
          handleDeleteAddress={() => {
            updateNodeData(id, { address: undefined });
            setIsDirty(true);
            setShowAddressDialog(false);
          }}
        />
      )}
    </div>
  );
}

export default React.memo(NoteItem);
